/**
 * The operator's own wallet ceilings, and the one rule the adjust dialog reads
 * them through (ADR-0002).
 *
 * Its own module rather than a helper inside the dialog: the rule is pure, it is
 * the part of this screen that decides whether money may move, and it is tested
 * on its own terms next door.
 */

import type {
  AdjustLimitKind,
  MyApprovalLimit,
  MyApprovalLimits,
} from '@b1fr0st/contracts/domain/approval-limits';

/**
 * The kinds and the row shape are `@b1fr0st/contracts` — the same definition
 * svc-admin files rows against and the two money edges enforce, so a rename on
 * the server is a compile error on this screen rather than a limit that quietly
 * stops matching. Re-exported because the dialog next door reads them through
 * this module, which is the one that owns the RULE.
 */
export type { AdjustLimitKind, MyApprovalLimit, MyApprovalLimits };

/**
 * Whether this amount is beyond what the operator may move alone.
 *
 * Three states, and the middle one is what the vocabulary keeps apart:
 * `unlimited` is a limit on record with no ceiling, `source: 'none'` is NO limit
 * on record — which ADR-0002 makes a refusal for every amount, not a licence.
 *
 * An UNREAD limit answers false. The server enforces regardless (WALLET-023), so
 * a console that could not reach `/approval-limits/me` must not invent a refusal
 * of its own; it just stops pre-empting one.
 *
 * `BigInt` on both sides, never `Number(maxAmount)` — comparing a ceiling
 * through a float is the silent 100x CLAUDE.md §2.2 forbids.
 */
export function beyondLimit(amountMinor: number, limit: MyApprovalLimit | undefined): boolean {
  if (!limit) return false;
  if (limit.unlimited) return false;
  if (limit.source === 'none' || limit.maxAmount === null) return true;
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) return false;
  return BigInt(amountMinor) > BigInt(limit.maxAmount);
}
